import React from 'react';

const DEMO_PRINT_LOGO_SRC = '/logo512.png';
const DEMO_PRINT_SURFACE_WIDTH_MM = 269;
const DEMO_PRINT_PAGE_MIN_HEIGHT_MM = 178;
const DEMO_PRINT_WATERMARK_TILE_SIZE_MM = 12;
const DEMO_PRINT_WATERMARK_LOGO_SIZE_MM = 8;
const DEMO_PRINT_WATERMARK_LOGO_COUNT = 360;

const pageBaseStyle = {
    minHeight: `${DEMO_PRINT_PAGE_MIN_HEIGHT_MM}mm`,
    boxSizing: 'border-box'
};

const brandWatermarkPatternStyle = {
    gridTemplateColumns: `repeat(auto-fill, ${DEMO_PRINT_WATERMARK_TILE_SIZE_MM}mm)`,
    gridAutoRows: `${DEMO_PRINT_WATERMARK_TILE_SIZE_MM}mm`,
    gap: '1mm',
    padding: '6mm',
    placeContent: 'start center',
    filter: 'grayscale(1) contrast(0.72) brightness(1.08)',
    opacity: 0.1,
    mixBlendMode: 'multiply',
    transform: 'scale(1.01)',
    transformOrigin: 'center'
};

const demoPrintWatermarkLogos = Array.from({ length: DEMO_PRINT_WATERMARK_LOGO_COUNT }, (_, index) => index);

const DemoPrintWatermarkPattern = () => {
    return (
        <div className="grid h-full w-full" style={brandWatermarkPatternStyle} aria-hidden="true">
            {demoPrintWatermarkLogos.map((tile) => (
                <div key={tile} className="flex items-center justify-center">
                    <img
                        src={DEMO_PRINT_LOGO_SRC}
                        alt=""
                        aria-hidden="true"
                        className="select-none object-contain"
                        style={{
                            width: `${DEMO_PRINT_WATERMARK_LOGO_SIZE_MM}mm`,
                            height: `${DEMO_PRINT_WATERMARK_LOGO_SIZE_MM}mm`
                        }}
                    />
                </div>
            ))}
        </div>
    );
};

const DemoPrintPageFrame = ({ pageLabel, title, children, className = '' }) => {
    return (
        <section
            className={[
                'demo-dedicated-print-page relative rounded-[26px] border border-[#ece4dc] bg-white px-5 py-4 text-[#171717] shadow-[0_14px_32px_rgba(15,23,42,0.05)]',
                className
            ].join(' ')}
            style={pageBaseStyle}
        >
            <div className="pointer-events-none absolute inset-[7px] overflow-hidden rounded-[20px]">
                <DemoPrintWatermarkPattern />
                <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[rgba(243,119,65,0.08)] via-[rgba(255,255,255,0.65)] to-transparent" />
            </div>

            <div className="relative z-10 flex min-h-full flex-col gap-3">
                <div className="flex items-center justify-between gap-4 border-b border-[#f0e8e1] pb-2.5">
                    <div className="flex items-center gap-3.5">
                        <div className="flex h-10 w-10 items-center justify-center rounded-[14px] border border-[#f1e8de] bg-white/90 p-1.5 shadow-sm">
                            <img src={DEMO_PRINT_LOGO_SRC} alt="Yancha" className="h-full w-full object-contain" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[var(--color-brand)]">Yancha Demo Report</p>
                            <h1 className="mt-0.5 text-[20px] leading-tight font-black text-[#171717] brand-font">{title}</h1>
                        </div>
                    </div>

                    <div className="text-right">
                        <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#8a7d74]">Preset Preview</p>
                        <p className="mt-0.5 text-[11px] font-semibold text-[#4b5563] brand-text">{pageLabel}</p>
                    </div>
                </div>

                <div className="relative z-10 min-h-0 flex-1">
                    {children}
                </div>
            </div>
        </section>
    );
};

const getRadarPoints = (values, size = 300, levels = 5) => {
    const center = size / 2;
    const radius = size * 0.32;
    const entries = values.map((item, index) => {
        const angle = ((Math.PI * 2) / values.length) * index - Math.PI / 2;
        const valueRadius = (item.value / 10) * radius;
        const x = center + Math.cos(angle) * valueRadius;
        const y = center + Math.sin(angle) * valueRadius;
        const labelRadius = radius + 38;
        const lx = center + Math.cos(angle) * labelRadius;
        const ly = center + Math.sin(angle) * labelRadius;

        return { ...item, x, y, lx, ly, angle };
    });

    const polygonPoints = entries.map(({ x, y }) => `${x},${y}`).join(' ');
    const grid = Array.from({ length: levels }, (_, levelIndex) => {
        const scale = (levelIndex + 1) / levels;
        return entries.map(({ angle }) => {
            const x = center + Math.cos(angle) * radius * scale;
            const y = center + Math.sin(angle) * radius * scale;
            return `${x},${y}`;
        }).join(' ');
    });

    return { center, radius, entries, polygonPoints, grid };
};

const DemoPrintHeroSection = ({ hero, archetype, riasec }) => {
    const snapshotRows = [
        { label: 'Profile Snapshot', value: archetype.name },
        { label: 'Vocation Lens', value: riasec.subtitle },
        { label: 'Preview Focus', value: 'Strengths, interests, and focused direction' }
    ];

    return (
        <section className="demo-dedicated-print-section rounded-[22px] border border-[#eee7df] bg-[radial-gradient(circle_at_top_left,_rgba(243,119,65,0.14),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(146,39,143,0.12),_transparent_36%),linear-gradient(180deg,_#ffffff_0%,_#fffdfb_100%)] px-5 py-4 shadow-[0_12px_24px_rgba(15,23,42,0.05)]">
            <div className="grid grid-cols-[minmax(0,1.35fr)_236px] gap-5 items-start">
                <div>
                    <span className="inline-flex rounded-full border border-[var(--color-brand)]/10 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-[var(--color-brand)]">
                        {hero.eyebrow}
                    </span>
                    <h1 className="mt-3 max-w-[500px] text-[30px] leading-[1.05] font-black text-[#171717] brand-font">
                        {hero.title}
                    </h1>
                    <p className="mt-2.5 max-w-[520px] text-[16px] leading-6 font-semibold text-[var(--color-subtext)] brand-text">
                        {hero.subtitle}
                    </p>
                    <p className="mt-2 max-w-[540px] text-[13px] leading-5 text-[#57534e] brand-text">
                        {hero.summary}
                    </p>

                    <div className="mt-4 flex flex-wrap gap-1.5">
                        {['Strengths', 'Interests', 'Direction'].map((item) => (
                            <span
                                key={item}
                                className="rounded-full border border-[var(--color-brand)]/10 bg-white px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-[var(--color-brand)] shadow-sm"
                            >
                                {item}
                            </span>
                        ))}
                    </div>
                </div>

                <aside className="rounded-[20px] border border-[var(--color-brand)]/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96)_0%,_rgba(248,244,255,0.95)_100%)] p-3.5 shadow-[0_10px_22px_rgba(146,39,143,0.08)]">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-[var(--color-brand)]">Profile Snapshot</p>
                            <h2 className="mt-1.5 text-[24px] leading-tight font-black text-[#171717] brand-font">
                                {archetype.name}
                            </h2>
                        </div>
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[18px] bg-gradient-to-br from-[var(--color-highlight)]/20 via-white to-[var(--color-brand)]/15 text-[22px] shadow-sm">
                            ✦
                        </div>
                    </div>

                    <div className="mt-3 space-y-2">
                        {snapshotRows.map((row) => (
                            <div
                                key={row.label}
                                className="rounded-[16px] border border-white/70 bg-white/85 px-3 py-2 shadow-sm"
                            >
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7c6f67]">{row.label}</p>
                                <p className="mt-0.5 text-[13px] leading-[1.35] font-semibold text-[#2b211a] brand-text">{row.value}</p>
                            </div>
                        ))}
                    </div>
                </aside>
            </div>
        </section>
    );
};

const DemoPrintRiasecPanel = ({ riasec }) => {
    const values = riasec.labels.map((label) => ({
        ...label,
        value: riasec.values[label.code] || 0
    }));
    const { center, radius, entries, polygonPoints, grid } = getRadarPoints(values);

    return (
        <section className="demo-dedicated-print-section rounded-[22px] border border-[#ece7e1] bg-white px-4 py-4 shadow-[0_12px_24px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-[208px_minmax(0,1fr)] gap-4 items-start">
                    <div className="rounded-[20px] border border-[#f1e8de] bg-[#faf7f4] p-3">
                        <svg viewBox="0 0 300 300" className="mx-auto w-full max-w-[196px]">
                            {grid.map((points, index) => (
                                <polygon
                                    key={index}
                                    points={points}
                                    fill="none"
                                    stroke="rgba(23,23,23,0.11)"
                                    strokeWidth="1"
                                />
                            ))}

                            {entries.map(({ x, y }, index) => (
                                <line
                                    key={index}
                                    x1={center}
                                    y1={center}
                                    x2={x + ((x - center) / (radius || 1)) * (radius - Math.hypot(x - center, y - center) + 2)}
                                    y2={y + ((y - center) / (radius || 1)) * (radius - Math.hypot(x - center, y - center) + 2)}
                                    stroke="rgba(23,23,23,0.11)"
                                    strokeWidth="1"
                                />
                            ))}

                            <polygon
                                points={polygonPoints}
                                fill="rgba(243,119,65,0.22)"
                                stroke="rgba(243,119,65,0.95)"
                                strokeWidth="3"
                                strokeLinejoin="round"
                            />

                            {entries.map(({ x, y, value, code }) => (
                                <g key={code}>
                                    <circle cx={x} cy={y} r="5" fill="#F37741" stroke="#fff" strokeWidth="2" />
                                    <text x={x} y={y - 12} textAnchor="middle" className="fill-[#171717] text-[10px] font-bold">
                                        {value}
                                    </text>
                                </g>
                            ))}

                            {entries.map(({ lx, ly, title, code }) => (
                                <text key={code} x={lx} y={ly} textAnchor="middle" className="fill-[#3f3f46] text-[10px] font-bold">
                                    {title}
                                </text>
                            ))}
                        </svg>
                    </div>

                    <div>
                        <p className="text-[11px] font-black uppercase tracking-[0.24em] text-[var(--color-brand)]">{riasec.title}</p>
                        <h2 className="mt-2 text-[21px] leading-tight font-black text-[#171717] brand-font">{riasec.subtitle}</h2>
                        <p className="mt-2 text-[12px] leading-[1.45] text-[#57534e] brand-text">{riasec.description}</p>
                    </div>
                </div>

                <div className="rounded-[20px] border border-[#f1e8de] bg-[#fffaf6] px-3.5 py-3">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#8a7d74]">RIASEC scores</p>
                    <div className="mt-2.5 grid grid-cols-3 gap-2">
                        {values.map(({ code, title, subtitle, value }) => (
                            <div key={code} className="rounded-[16px] border border-[#f1e8de] bg-[#fffdfb] px-2.5 py-2.5">
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        <p className="text-[11px] font-black text-[#171717] brand-font">{title}</p>
                                        <p className="text-[9px] uppercase tracking-[0.1em] text-[#7c6f67]">{subtitle}</p>
                                    </div>
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--color-highlight)]/12 text-[13px] font-black text-[#b45309]">
                                        {value}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

const DemoPrintArchetypePanel = ({ archetype }) => {
    return (
        <section className="demo-dedicated-print-section rounded-[22px] border border-[var(--color-highlight)]/35 bg-gradient-to-br from-white via-[#fffaf7] to-[#f8f4ff] px-4 py-4 shadow-[0_12px_24px_rgba(243,119,65,0.12)]">
            <span className="inline-flex rounded-full bg-gradient-to-r from-[var(--color-highlight)] to-[var(--color-brand)] px-3 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-white shadow-sm">
                {archetype.banner}
            </span>

            <div className="mt-3 flex items-start gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] border border-orange-100 bg-white text-[30px] shadow-md">
                    💡
                </div>

                <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#7c6f67]">Core Identity</p>
                    <h2 className="mt-1 text-[24px] leading-tight font-black text-[#171717] brand-font">{archetype.name}</h2>
                    <p className="mt-2 text-[14px] leading-[1.45] font-semibold text-[#171717] brand-text">{archetype.identity}</p>
                </div>
            </div>

            <div className="mt-3 rounded-[18px] border border-[var(--color-highlight)]/15 bg-white px-3.5 py-3 shadow-sm">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[var(--color-brand)]">{archetype.strengthTitle}</p>
                <p className="mt-1.5 text-[12px] leading-[1.45] text-[#57534e] brand-text">{archetype.strengthText}</p>
            </div>

            <div className="mt-3 rounded-[18px] border border-white/70 bg-white/90 px-3.5 py-3 shadow-sm">
                <p className="text-[13px] leading-5 font-semibold text-[#171717] brand-text">{archetype.formula}</p>
            </div>

            <ul className="mt-3 space-y-2">
                {archetype.bullets.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-[12px] leading-[1.4] text-[#57534e] brand-text">
                        <span className="mt-1 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[var(--color-brand)]/10 text-[11px] font-black text-[var(--color-brand)]">•</span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
};

const DemoPrintCompassPanel = ({ compass }) => {
    return (
        <section className="demo-dedicated-print-section rounded-[22px] border border-[#ece7e1] bg-white px-4 py-4 shadow-[0_12px_24px_rgba(15,23,42,0.04)]">
            <div className="flex items-end justify-between gap-3">
                <h2 className="text-[24px] leading-tight font-black text-[#171717] brand-font">{compass.title}</h2>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#7c6f67]">Behavioral Summary</p>
            </div>

            <div className="mt-3.5 grid grid-cols-4 gap-2.5">
                {compass.pillars.map((pillar) => (
                    <div
                        key={pillar.code}
                        className="demo-dedicated-print-card rounded-[18px] border border-[#f0ebe5] bg-[#fffdfb] px-3 py-3 shadow-sm"
                    >
                        <div className="flex items-center gap-2.5">
                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${pillar.accent} text-[16px] font-black text-white shadow-md`}>
                                {pillar.code}
                            </div>
                            <div>
                                <h3 className="text-[14px] font-black text-[#171717] brand-font">{pillar.title}</h3>
                            </div>
                        </div>
                        <p className="mt-2.5 text-[12px] leading-[1.45] text-[#57534e] brand-text">{pillar.description}</p>
                    </div>
                ))}
            </div>
        </section>
    );
};

const DemoPrintCareerMatrixPanel = ({ careerMatrix }) => {
    return (
        <section className="demo-dedicated-print-section rounded-[22px] border border-[#ece7e1] bg-white px-4 py-4 shadow-[0_12px_24px_rgba(15,23,42,0.04)]">
            <div className="mb-3 flex items-end justify-between gap-3">
                <h2 className="text-[24px] leading-tight font-black text-[#171717] brand-font">{careerMatrix.title}</h2>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#7c6f67]">Decision Making</p>
            </div>

            <div className="grid gap-2">
                <div className="grid gap-2" style={{ gridTemplateColumns: '126px repeat(3, minmax(0, 1fr))' }}>
                    <div></div>
                    {careerMatrix.columns.map((column) => (
                        <div
                            key={column}
                            className="rounded-[16px] bg-gradient-to-r from-[#fff4eb] to-[#f7efff] px-2.5 py-2 text-center text-[11px] font-black text-[#171717] brand-text"
                        >
                            {column}
                        </div>
                    ))}
                </div>

                {careerMatrix.rows.map((row) => (
                    <div key={row.title} className="grid gap-2 items-stretch" style={{ gridTemplateColumns: '126px repeat(3, minmax(0, 1fr))' }}>
                        <div className="flex items-center justify-center rounded-[16px] border border-[#f1e7de] bg-[#faf7f4] px-2.5 py-3 text-center text-[11px] font-black text-[#171717] brand-text">
                            {row.title}
                        </div>

                        {row.cells.map((cell, index) => (
                            <div
                                key={`${row.title}-${index}`}
                                className="rounded-[16px] border px-3 py-3 text-[11px] leading-[1.4] text-[#4b5563] brand-text shadow-sm"
                                style={{
                                    background: index === 0 ? '#fff7f0' : index === 1 ? '#eefaf5' : '#f8f1ff',
                                    borderColor: index === 0 ? '#fed7aa' : index === 1 ? '#bbf7d0' : '#e9d5ff'
                                }}
                            >
                                {cell}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </section>
    );
};

const DemoResultsPrintPage = ({ preset }) => {
    const { hero, riasec, archetype, compass, careerMatrix } = preset;

    return (
        <div className="demo-dedicated-print-portal" aria-hidden="true">
            <div className="demo-dedicated-print-surface mx-auto flex flex-col gap-4 bg-white text-[#171717]" style={{ width: `${DEMO_PRINT_SURFACE_WIDTH_MM}mm` }}>
                <DemoPrintPageFrame pageLabel="Page 1 of 3" title="Career Compass Overview">
                    <DemoPrintHeroSection hero={hero} archetype={archetype} riasec={riasec} />
                </DemoPrintPageFrame>

                <DemoPrintPageFrame pageLabel="Page 2 of 3" title="RIASEC and Archetype Snapshot">
                    <div className="demo-dedicated-print-grid grid grid-cols-[minmax(0,1.03fr)_minmax(0,0.97fr)] gap-4 items-start">
                        <div className="demo-dedicated-print-card min-w-0">
                            <DemoPrintRiasecPanel riasec={riasec} />
                        </div>

                        <div className="demo-dedicated-print-card min-w-0">
                            <DemoPrintArchetypePanel archetype={archetype} />
                        </div>
                    </div>
                </DemoPrintPageFrame>

                <DemoPrintPageFrame pageLabel="Page 3 of 3" title="Behavior and Career Constellation">
                    <div className="flex flex-col gap-3">
                        <DemoPrintCompassPanel compass={compass} />
                        <DemoPrintCareerMatrixPanel careerMatrix={careerMatrix} />

                        <div className="pt-0.5 text-center">
                            <p className="text-[10px] uppercase tracking-[0.22em] text-gray-400 brand-text">
                                Demo preview — full assessment report will be handled separately
                            </p>
                        </div>
                    </div>
                </DemoPrintPageFrame>
            </div>
        </div>
    );
};

export default DemoResultsPrintPage;
